import { Fragment, useState } from "react";
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Transition } from "@headlessui/react";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/24/outline";

import classNames from "../../../utils/classNames";
import normalizeString from "../../../utils/normaliseString";

export interface OnPrimaryComboBoxOption {
  id: any;
  name: string;
}

interface OnPrimaryComboBoxProps {
  label?: string;
  options: OnPrimaryComboBoxOption[];
  value: any;
  onChange: (value: any) => void;
  icon?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  classes?: string;
}

/**
 * Searchable combo box designed to sit on a primary-coloured surface, matching
 * {@link OnPrimarySelect} and {@link OnPrimaryButton}.
 */
export default function OnPrimaryComboBox({
  label,
  options,
  value,
  onChange,
  icon,
  placeholder = "Search...",
  disabled = false,
  classes = "",
}: OnPrimaryComboBoxProps) {
  const [query, setQuery] = useState("");

  const filteredOptions =
    query === ""
      ? options
      : options.filter(
          (option) =>
            normalizeString(option.name)?.includes(normalizeString(query)) ||
            option.id?.toString().includes(query)
        );

  return (
    <div className={classNames("w-full", classes)}>
      <Combobox value={value} onChange={onChange} disabled={disabled}>
        <div className="relative w-full">
          <div
            className={classNames(
              "flex w-full items-center gap-2 rounded-md border border-white/25 bg-white/10 px-3 py-2.5 text-sm font-medium text-white transition-colors",
              "hover:bg-white/20 focus-within:bg-white/20 focus-within:ring-2 focus-within:ring-white/60",
              disabled ? "cursor-not-allowed opacity-70" : ""
            )}
          >
            {icon && <span className="flex h-4 w-4 shrink-0 items-center justify-center">{icon}</span>}
            {label && <span className="shrink-0 truncate text-white">{label}</span>}
            <ComboboxInput
              className="min-w-0 flex-1 border-0 bg-transparent p-0 text-right text-sm text-white placeholder:text-white/60 focus:border-0 focus:outline-none focus:ring-0 disabled:cursor-not-allowed"
              displayValue={(id: any) =>
                id === "" || id === null || id === undefined ? "" : options.find((o) => o.id === id)?.name ?? ""
              }
              onChange={(event) => setQuery(event.target.value)}
              placeholder={placeholder}
              autoComplete="off"
            />
            <ComboboxButton className="flex shrink-0 items-center">
              <ChevronDownIcon className="h-4 w-4 text-white/70" aria-hidden="true" />
            </ComboboxButton>
          </div>

          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery("")}
          >
            <ComboboxOptions className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              {filteredOptions.length === 0 && query !== "" ? (
                <div className="relative cursor-default select-none px-4 py-2 text-gray-700">Nothing found.</div>
              ) : (
                filteredOptions.slice(0, 100).map((option) => (
                  <ComboboxOption
                    key={option.id ?? option.name}
                    value={option.id}
                    className={({ active }) =>
                      classNames(
                        active ? "bg-primary-100 text-gray-900" : "text-gray-900",
                        "relative cursor-pointer select-none py-2 pl-8 pr-4"
                      )
                    }
                  >
                    {({ selected }) => (
                      <>
                        <span className={classNames(selected ? "font-semibold" : "font-normal", "block truncate")}>
                          {option.name}
                        </span>
                        {selected ? (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-1.5 text-primary-600">
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </ComboboxOption>
                ))
              )}
            </ComboboxOptions>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
}
